import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/AdminAuth.css";
import { ADMIN_LOGIN_API, ADMIN_REGISTER_API } from "../config/api";
import logo from "../images/logo.png";

function AdminAuth() {
  const navigate = useNavigate();

  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // 🔐 LOGIN
  const handleLogin = async () => {
    const res = await fetch(ADMIN_LOGIN_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: formData.email,
        password: formData.password
      })
    });

    const data = await res.json();

    if (data.status === "success") {
      localStorage.setItem("adminAuth", "true");
      localStorage.setItem("adminId", data.admin_id);
      localStorage.setItem("adminName", data.name || "Admin");
      localStorage.setItem("adminEmail", data.email || "");
      navigate("/admin/home");
    } else {
      setError(data.message || "Invalid email or password");
    }
  };

  // 🆕 REGISTER
  const handleRegister = async () => {
    const res = await fetch(ADMIN_REGISTER_API, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData)
    });

    const data = await res.json();

    if (data.status === "success") {
      alert("Registered successfully 🎉 Please login now");
      setIsLogin(true);
      setFormData({ name: "", email: "", password: "" });
    } else {
      setError(data.message || "Registration failed. Please try again.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.email || !formData.password) {
      setError("Email and Password required");
      return;
    }

    if (!isLogin && !formData.name) {
      setError("Name required");
      return;
    }

    try {
      setLoading(true);

      if (isLogin) {
        await handleLogin();
      } else {
        await handleRegister();
      }
    } catch (err) {
      setError("Unable to connect to server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-portal-root">
      <div className="admin-login-viewport">
        {/* Full-viewport Symmetrical Temple Background */}
        <div className="temple-bg-layer" aria-hidden="true" />
        <div className="temple-vignette-overlay" aria-hidden="true" />

        {/* Traditional Hanging Brass Lamp (Deepam) Glows */}
        <div className="ambient-lamp-glow lamp-glow-left" aria-hidden="true" />
        <div className="ambient-lamp-glow lamp-glow-right" aria-hidden="true" />

        {/* Subtle Antique Gold Viewport Corner Filigrees */}
        <svg className="corner-filigree corner-tl" viewBox="0 0 100 100" fill="none" aria-hidden="true">
          <path d="M 0 0 L 80 0 C 45 5 15 35 10 70 L 10 100 L 0 100 Z" fill="rgba(201, 154, 61, 0.15)" />
          <path d="M 8 8 L 65 8 C 40 12 18 34 14 60 L 14 85" stroke="#C99A3D" strokeWidth="1" strokeDasharray="3 2" />
        </svg>
        <svg className="corner-filigree corner-tr" viewBox="0 0 100 100" fill="none" aria-hidden="true">
          <path d="M 0 0 L 80 0 C 45 5 15 35 10 70 L 10 100 L 0 100 Z" fill="rgba(201, 154, 61, 0.15)" />
          <path d="M 8 8 L 65 8 C 40 12 18 34 14 60 L 14 85" stroke="#C99A3D" strokeWidth="1" strokeDasharray="3 2" />
        </svg>
        <svg className="corner-filigree corner-bl" viewBox="0 0 100 100" fill="none" aria-hidden="true">
          <path d="M 0 0 L 80 0 C 45 5 15 35 10 70 L 10 100 L 0 100 Z" fill="rgba(201, 154, 61, 0.15)" />
          <path d="M 8 8 L 65 8 C 40 12 18 34 14 60 L 14 85" stroke="#C99A3D" strokeWidth="1" strokeDasharray="3 2" />
        </svg>
        <svg className="corner-filigree corner-br" viewBox="0 0 100 100" fill="none" aria-hidden="true">
          <path d="M 0 0 L 80 0 C 45 5 15 35 10 70 L 10 100 L 0 100 Z" fill="rgba(201, 154, 61, 0.15)" />
          <path d="M 8 8 L 65 8 C 40 12 18 34 14 60 L 14 85" stroke="#C99A3D" strokeWidth="1" strokeDasharray="3 2" />
        </svg>

        {/* Centered Royal Burgundy & Gold Login Panel */}
        <div className="royal-login-card-wrapper">
          <div className="royal-login-card">

            {/* Decorative Gold Arch Crown Rising Above the Panel */}
            <div className="card-arch-crown-container">
              {/* Symmetrical Arch SVG Outline with Pointed Apex Crown */}
              <svg className="arch-svg-outline" viewBox="0 0 320 96" fill="none" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="goldArchGrad" x1="0" y1="96" x2="320" y2="96" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#C99A3D" stopOpacity="0.4" />
                    <stop offset="15%" stopColor="#D49A36" stopOpacity="0.8" />
                    <stop offset="45%" stopColor="#F6D184" />
                    <stop offset="50%" stopColor="#FFEFC7" />
                    <stop offset="55%" stopColor="#F6D184" />
                    <stop offset="85%" stopColor="#D49A36" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="#C99A3D" stopOpacity="0.4" />
                  </linearGradient>
                  <linearGradient id="archFillGrad" x1="160" y1="0" x2="160" y2="96" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#320912" />
                    <stop offset="60%" stopColor="#27070D" />
                    <stop offset="100%" stopColor="#25060C" />
                  </linearGradient>
                </defs>
                {/* Arch Body fill & outline */}
                <path
                  d="M 12 96 C 36 96 68 84 98 58 C 122 36 142 16 156 8 L 160 2 L 164 8 C 178 16 198 36 222 58 C 252 84 284 96 308 96 Z"
                  fill="url(#archFillGrad)"
                  stroke="url(#goldArchGrad)"
                  strokeWidth="1.2"
                />
                {/* Pointed Arch Crown Finial */}
                <circle cx="160" cy="2" r="2.2" fill="#FFEFC7" />
              </svg>

              {/* Faint Torana / Mandala Halo Watermark */}
              <svg className="arch-mandala-watermark" viewBox="0 0 170 85" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M 85 80 m -60, 0 a 60,60 0 1,1 120,0" stroke="#C99A3D" strokeWidth="0.7" strokeDasharray="3 3" />
                <path d="M 85 80 m -45, 0 a 45,45 0 1,1 90,0" stroke="#F6D184" strokeWidth="0.5" />
                <path d="M 85 80 m -30, 0 a 30,30 0 1,1 60,0" stroke="#C99A3D" strokeWidth="0.5" strokeDasharray="2 2" />
                <circle cx="85" cy="20" r="2" fill="#E5A347" />
                <circle cx="55" cy="35" r="1.5" fill="#E5A347" />
                <circle cx="115" cy="35" r="1.5" fill="#E5A347" />
              </svg>

              {/* Vaithikasri Branding & Sacred Emblem */}
              <div className="arch-backdrop-mask">
                <div className="brand-crest-wrapper">
                  <div className="emblem-halo">
                    {/* Real Vaithikasri Logo Image inside Gold Bezel */}
                    <img
                      src={logo}
                      alt="Vaithikasri Emblem"
                      className="vaithikasri-logo-img"
                    />

                    {/* Sacred Lotus Petals & Flame SVG overlay around the logo */}
                    <svg className="lotus-flame-ornament" viewBox="0 0 82 86" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                      {/* Sacred Jyoti (Flame) rising above */}
                      <path
                        d="M 41 4 C 44 11 48 16 46 22 C 44 26 42 27 41 27 C 40 27 38 26 36 22 C 34 16 38 11 41 4 Z"
                        fill="url(#flameGrad)"
                        filter="drop-shadow(0 0 4px #F6D184)"
                      />
                      <path
                        d="M 41 10 C 42.5 14 44 18 43 21 C 42 23 41.5 24 41 24 C 40.5 24 40 23 39 21 C 38 18 39.5 14 41 10 Z"
                        fill="#FFFDE6"
                      />

                      {/* Golden Lotus Petals cupping the base */}
                      <path d="M 41 72 C 36 67 28 66 23 70 C 27 75 35 77 41 77 C 47 77 55 75 59 70 C 54 66 46 67 41 72 Z" fill="#C99A3D" opacity="0.9" />
                      <path d="M 41 70 C 38 64 30 63 26 67 C 30 71 36 73 41 73 C 46 73 52 71 56 67 C 52 63 44 64 41 70 Z" fill="#F6D184" />
                      <circle cx="41" cy="74" r="1.5" fill="#FFEFC7" />

                      <defs>
                        <linearGradient id="flameGrad" x1="41" y1="4" x2="41" y2="27" gradientUnits="userSpaceOnUse">
                          <stop offset="0%" stopColor="#FFF2D1" />
                          <stop offset="35%" stopColor="#F6D184" />
                          <stop offset="70%" stopColor="#E5A347" />
                          <stop offset="100%" stopColor="#C9442A" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>

                  {/* Brand Typography */}
                  <h2 className="brand-title">Vaithikasri</h2>

                  {/* Delicate Antique Gold Horizontal Divider */}
                  <div className="brand-gold-divider" aria-hidden="true">
                    <span className="divider-line left" />
                    <svg className="divider-flourish" viewBox="0 0 24 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <polygon points="12,1 15,5 12,9 9,5" fill="#D49A36" />
                      <circle cx="5" cy="5" r="1.2" fill="#C99A3D" />
                      <circle cx="19" cy="5" r="1.2" fill="#C99A3D" />
                    </svg>
                    <span className="divider-line right" />
                  </div>
                </div>
              </div>
            </div>

            {/* Heading Section */}
            <div className="card-header-section">
              <h1 className="login-heading">{isLogin ? "Admin Login" : "Admin Register"}</h1>
              <p className="login-subtitle">Vaithikasri Administrative Portal</p>
            </div>

            {/* Error Message Alert */}
            {error && (
              <div className="error-banner" role="alert">
                <i className="bi bi-exclamation-triangle-fill" aria-hidden="true"></i>
                <span>{error}</span>
              </div>
            )}

            {/* Authentication Form */}
            <form onSubmit={handleSubmit} className="auth-form" noValidate autoComplete="off">
              {/* NAME (REGISTER ONLY) */}
              {!isLogin && (
                <div className="field-group">
                  <label htmlFor="admin-name" className="field-label">NAME</label>
                  <div className="auth-input-wrapper">
                    <span className="auth-input-icon" aria-hidden="true">
                      <i className="bi bi-person"></i>
                    </span>
                    <input
                      id="admin-name"
                      type="text"
                      name="name"
                      className="auth-input-field"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required={!isLogin}
                      autoComplete="name"
                      style={{
                        background: 'transparent',
                        backgroundColor: 'transparent',
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                        borderRadius: 0,
                        color: '#FFF8EF',
                        padding: 0
                      }}
                    />
                  </div>
                </div>
              )}

              {/* EMAIL ADDRESS */}
              <div className="field-group">
                <label htmlFor="admin-email" className="field-label">EMAIL ADDRESS</label>
                <div className="auth-input-wrapper">
                  <span className="auth-input-icon" aria-hidden="true">
                    <i className="bi bi-envelope"></i>
                  </span>
                  <input
                    id="admin-email"
                    type="email"
                    name="email"
                    className="auth-input-field"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    style={{
                      background: 'transparent',
                      backgroundColor: 'transparent',
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                      borderRadius: 0,
                      color: '#FFF8EF',
                      padding: 0
                    }}
                  />
                </div>
              </div>

              {/* PASSWORD */}
              <div className="field-group">
                <label htmlFor="admin-password" className="field-label">PASSWORD</label>
                <div className="auth-input-wrapper">
                  <span className="auth-input-icon" aria-hidden="true">
                    <i className="bi bi-lock"></i>
                  </span>
                  <input
                    id="admin-password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="auth-input-field"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete={isLogin ? "current-password" : "new-password"}
                    style={{
                      background: 'transparent',
                      backgroundColor: 'transparent',
                      border: 'none',
                      outline: 'none',
                      boxShadow: 'none',
                      borderRadius: 0,
                      color: '#FFF8EF',
                      padding: 0
                    }}
                  />
                  <button
                    type="button"
                    className="auth-input-toggle-btn"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={showPassword ? "bi bi-eye-slash" : "bi bi-eye"} aria-hidden="true"></i>
                  </button>
                </div>
              </div>

              {/* LUXURIOUS GOLD SUBMIT BUTTON */}
              <button
                type="submit"
                className="gold-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" style={{ width: "1.05rem", height: "1.05rem", borderWidth: "2px", color: "#27070D" }}></span>
                    <span>{isLogin ? "Signing in..." : "Registering..."}</span>
                  </>
                ) : isLogin ? (
                  <>
                    <span>Login</span>
                    <i className="bi bi-box-arrow-in-right" aria-hidden="true"></i>
                  </>
                ) : (
                  <>
                    <span>Register</span>
                    <i className="bi bi-person-plus-fill" aria-hidden="true"></i>
                  </>
                )}
              </button>
            </form>

            {/* SWITCH MODE FOOTER */}
            <p className="auth-footer">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <button
                type="button"
                className="auth-switch-link"
                style={{ background: "none", border: "none", padding: 0 }}
                onClick={() => {
                  setIsLogin(!isLogin);
                  setError("");
                }}
              >
                {isLogin ? "Register" : "Login"}
              </button>
            </p>

            {/* Bottom Symmetrical Antique Gold Ornament */}
            <div className="card-bottom-ornament" aria-hidden="true">
              <svg className="ornament-svg" viewBox="0 0 76 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Center diamond & petal flower */}
                <path d="M 38 4 C 39.5 7 39.5 12 38 15 C 36.5 12 36.5 7 38 4 Z" fill="#F6D184" />
                <path d="M 33 9.5 C 35.5 8 40.5 8 43 9.5 C 40.5 11 35.5 11 33 9.5 Z" fill="#F6D184" />
                <circle cx="38" cy="9.5" r="1.5" fill="#FFF8EF" />
                
                {/* Left ornate scroll tendrils */}
                <path d="M 34 9.5 C 28 7.5 22 4.5 16 7.5 C 11 10 10 15.5 14 17.5 C 18 19.5 23 16.5 20 12.5 C 18 10 14 11.5 13 13.5" stroke="#C99A3D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M 30 10.5 C 24 13.5 20 15.5 16 14.5" stroke="#D49A36" strokeWidth="1" strokeLinecap="round" />
                <circle cx="11" cy="14.5" r="1" fill="#E5A347" />

                {/* Right ornate scroll tendrils (mirrored) */}
                <path d="M 42 9.5 C 48 7.5 54 4.5 60 7.5 C 65 10 66 15.5 62 17.5 C 58 19.5 53 16.5 56 12.5 C 58 10 62 11.5 63 13.5" stroke="#C99A3D" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M 46 10.5 C 52 13.5 56 15.5 60 14.5" stroke="#D49A36" strokeWidth="1" strokeLinecap="round" />
                <circle cx="65" cy="14.5" r="1" fill="#E5A347" />

                {/* Lower center droplet */}
                <path d="M 38 15 C 39 18 39.5 21 38 24 C 36.5 21 37 18 38 15 Z" fill="#F6D184" />
                <circle cx="38" cy="25" r="1" fill="#E5A347" />
              </svg>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminAuth;
